import React, { ChangeEvent, FC, Fragment, useState, useMemo, useEffect } from 'react';
import {
  POLLS_RESPONSES_LIMIT_MESSAGE,
  MAX_RESPONSES_LIMIT,
  PollChoiceStatus,
} from '../../../constans/polls-constants';
import AutoTextArea from '../../shared/auto-textarea/auto-textarea';
import { useStore } from './../../../store/store-provider';
import PollEntity from '../../../store/polls-store/poll-entity';
import ManagePollHeader from './manage-poll-header/manage-poll-header';
import PollResponseList from '../poll-response-list/poll-response-list';
import { observer } from 'mobx-react';
import ResponseEntity, { IResponseItem } from '../../../store/polls-store/response-entity';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import Modal from '../../shared/modal/modal';
import { sortByOrder } from '../../../utils/helpers/polls-helper';
import { ModalTypes } from '../../../constans/modal.constants';
import './manage-poll.scss';
import { getMixpanelService } from '../../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../../constans/mixpanel-contants';

interface IManagePollProps {
  poll: PollEntity | null;
}

const ManagePoll: FC<IManagePollProps> = ({ poll }) => {
  const [isValidResponses, setIsvalidResponses] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isValidFiltered, setIsValidFiltered] = useState<boolean>(true);
  const { pollsStore } = useStore();
  const pollEntity = useMemo(() => poll ? new PollEntity(poll?.serialize()) : new PollEntity(), [poll]);
  const isResponsesLimitExceeded = pollEntity.responses.length > MAX_RESPONSES_LIMIT;
  const isEdit = !!pollsStore.selectedPollId;
  const manageTitle = `${isEdit ? 'Edit' : 'Create'} Poll`;

  useEffect(() => {
    if (pollsStore.selectedPollId) {
      setIsvalidResponses(true);

      if (!poll?.responses.length) pollsStore.getPollChoisesList(pollsStore.selectedPollId);
    }
  }, []);

  useEffect(() => {
    const isValidPollValue = !!pollEntity.isPollValueValid();
    const isValid = isValidPollValue && isValidResponses && !!pollEntity.responses.length && isValidFiltered;

    if (isDisabled || !isValid) setIsDisabled(!isValid);

  }, [isValidResponses, isValidFiltered, pollEntity.value, pollEntity.responses]);

  const clearCreateResponseInput = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    e.target.value = '';
  };

  const handleChangeQuestion = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    pollEntity.setQuestion(e.target.value);
  };

  const createResponse = (e: ChangeEvent<HTMLTextAreaElement>): IResponseItem => {
    return {
      pollId: pollEntity.getId(),
      pollChoiceId: `response-${uuidv4()}`,
      value: e.target.value,
      order: pollEntity.responses.length,
      isCustomResponse: false,
      status: PollChoiceStatus.ACTIVE
    };
  };

  const addResponse = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    const response = createResponse(e);
    const responseEntity = new ResponseEntity(response);
    pollEntity.addResponse(responseEntity);
    setIsvalidResponses(responseEntity.checkIsResponseValid());
    setIsValidFiltered(true);
    clearCreateResponseInput(e);
  };

  const onSubmit = async (): Promise<void> => {
    if (isResponsesLimitExceeded) {
      pollsStore.shouldShowNotificationModal = true;
      return;
    }
    pollsStore.toggleManagePollModal();
    if (isEdit) {
      await pollsStore.updatePollAndPollChoices(pollEntity);
      getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.SAVED_EDIT);
    } else {
      await pollsStore.createPoll(pollEntity);
      getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.SAVED_CREATE);
    }
    updatePollSnapshot();
  };

  const updatePollSnapshot = () => {
    const newPolObject = pollEntity.serialize();
    if (poll) {
      poll.deserialize(newPolObject);
      poll.snapshot = newPolObject;
    }
  };

  const cancelHandler = (): void => {
    pollsStore.toggleManagePollModal();
    if (poll) poll.deserialize(poll.snapshot);
    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.CANCELED);
  };

  /* eslint-disable @typescript-eslint/no-unused-expressions */
  const deleteResponseHandler = (id: string): void => {
    if (pollsStore.selectedPollId) {
      pollEntity.findResponseById(id)?.setStatus(PollChoiceStatus.DELETED);
      setIsValidFiltered(!!pollEntity.getFilteredResponses().length);
    } else {
      pollEntity.deleteResponseById(id);
    }
  };
  /* eslint-enable @typescript-eslint/no-unused-expressions */

  const hideNotificationModal = (): void => {
    pollsStore.hideNotificationModal();
    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.ERROR_MAX_RESPONSES);
  };

  return (
    <Fragment>
      <div className='overlap'/>
      <div className='manage-poll'>
        <ManagePollHeader
          cancelHandler={cancelHandler}
          onSubmit={onSubmit}
          isDisabled={isDisabled}
          onChangeQuestion={handleChangeQuestion}
          question={pollEntity.value}
          title={manageTitle}
        />
        <div className='manage-poll-body'>
          {!pollsStore.isLoading &&
            <div className='manage-poll-responses-list'>
              <PollResponseList
                responseList={sortByOrder(pollEntity.getFilteredResponses())}
                setResponses={pollEntity.setResponses}
                deleteResponseById={deleteResponseHandler}
                validation={setIsvalidResponses}
              />
              <AutoTextArea
                className='manage-poll-response-empty manage-poll-response-input'
                classNameWrapper={classNames('empty', { 'first': !pollEntity.responses.length })}
                placeholder='Enter response…'
                onChange={addResponse}
              />
            </div>
          }
        </div>
      </div>
      {isResponsesLimitExceeded && pollsStore.shouldShowNotificationModal
        ? <Modal
          cancelAction={hideNotificationModal}
          modalType={ModalTypes.NOTIFICATION}
          htmlString={POLLS_RESPONSES_LIMIT_MESSAGE}
          />
        : null
      }
    </Fragment>
  );
};

export default observer(ManagePoll);